import { useCallback, useEffect, useRef, useState } from "react";
import { Avatar, greeting } from "@/components/chat/Avatar";
import { PushPrompt } from "@/components/chat/PushPrompt";
import { toast } from "sonner";
import { BrandLockup } from "@/components/Brand";
import { ParticipantMessages, Bubble } from "@/components/chat/MessageList";
import { PixBar } from "@/components/chat/PixBar";
import { Composer } from "@/components/chat/Composer";
import { RegisterDialog } from "@/components/chat/RegisterDialog";
import { AccountDialog } from "@/components/chat/AccountDialog";
import { InstallApp } from "@/components/chat/InstallApp";
import { Outbox } from "@/components/chat/Outbox";
import { publicApi, getPToken, setPToken, errMsg } from "@/lib/api";
import { captureOrigin } from "@/lib/origin";
import { fmtTime } from "@/lib/format";

const WELCOME = {
  id: "welcome", text: "Bem-vindo(a) à Central Digital da Igreja Unida Deus Proverá. Envie sua mensagem, pedido de oração, testemunho, fotografia, áudio, documento ou comprovante Pix. A equipe responderá por aqui.",
  created_at: new Date().toISOString(), kind: "text",
};

const Header = ({ settings, me, onAccount }) => (
  <header className="flex h-[68px] shrink-0 items-center gap-3 border-b border-[#C9A24A]/30 bg-zap-deep px-3 text-white sm:h-[76px] sm:px-5">
    <h1 data-testid="chat-title" className="min-w-0 flex-1">
      <BrandLockup hasLogo={settings?.has_logo} version={settings?.logo_version} logoClass="h-[42px] min-[380px]:h-[52px] sm:h-[60px]" />
    </h1>
    <InstallApp />
    {me && (
      <button data-testid="account-button" onClick={onAccount} aria-label="Meus dados" className="flex h-12 w-12 items-center justify-center rounded-full hover:bg-white/10">
        <Avatar client={publicApi} path={me.avatar_file_id ? `/public/files/${me.avatar_file_id}` : null} name={me.name}
          className="h-10 w-10 text-sm ring-2 ring-[#C9A24A]/70" testId="header-avatar" />
      </button>
    )}
  </header>
);

const Greeting = ({ me }) => (
  <div data-testid="participant-greeting" className="shrink-0 border-b border-zap-line bg-zap-mist px-4 py-1.5 text-[15px] text-zap-deep sm:px-6">
    {greeting()}, <span className="font-semibold">{me.name.split(" ")[0]}</span>
  </div>
);

export default function ParticipantChat() {
  const [settings, setSettings] = useState(null);
  const [me, setMe] = useState(null);
  const [messages, setMessages] = useState([]);
  const [askRegister, setAskRegister] = useState(false);
  const [account, setAccount] = useState(false);
  const [outbox, setOutbox] = useState([]);
  const pending = useRef(null);
  const scroller = useRef(null);
  useEffect(() => {
    if (outbox.length) scroller.current?.scrollTo({ top: scroller.current.scrollHeight });
  }, [outbox.length]);

  const known = useRef(null);
  const load = useCallback(async () => {
    if (known.current === false && !getPToken()) return;
    try {
      const [m, msgs] = await Promise.all([publicApi.get("/public/me"), publicApi.get("/public/messages")]);
      known.current = true;
      setMe(m.data);
      setMessages(msgs.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        known.current = false;
        setPToken(null);
        setMe(null);
        setMessages([]);
      }
    }
  }, []);

  useEffect(() => {
    captureOrigin();
    publicApi.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {});
    load();
    const t = setInterval(() => document.visibilityState === "visible" && load(), 8000);
    return () => clearInterval(t);
  }, [load]);

  const post = async (draft) => {
    const fd = new FormData();
    fd.append("kind", draft.kind);
    fd.append("text", draft.text || "");
    if (draft.file) fd.append("file", draft.file);
    const { data } = await publicApi.post("/public/messages", fd);
    return data;
  };

  const patchItem = (localId, patch) => setOutbox((list) => list.map((x) => (x.localId === localId ? { ...x, ...patch } : x)));

  const dispatch = async (item, authMode = null) => {
    patchItem(item.localId, { status: "sending", error: "" });
    try {
      const data = await post(item.draft);
      await load();
      setOutbox((list) => list.filter((x) => x.localId !== item.localId));
      const at = fmtTime(data.created_at);
      toast.success(authMode === "register" ? `Cadastro confirmado. Participação enviada às ${at}.`
        : authMode === "recover" ? `Acesso recuperado. Participação enviada às ${at}.`
        : authMode === "return" ? `Que bom ter você de volta. Participação enviada às ${at}.` : `Recebido às ${at}.`, { id: authMode ? "auth" : "sent" });
      return true;
    } catch (e) {
      let error = errMsg(e, "sem conexão com a internet");
      if (e?.response?.status === 401) {
        setPToken(null);
        setMe(null);
        setMessages([]);
        error = "identifique-se para enviar";
      }
      patchItem(item.localId, { status: "failed", error });
      return false;
    }
  };

  const enqueue = async (drafts, authMode = null) => {
    const items = drafts.map((draft, i) => ({ localId: `${Date.now()}-${i}-${Math.random()}`, draft, status: "sending", error: "" }));
    setOutbox((list) => [...list, ...items]);
    for (const it of items) {
      await dispatch(it, authMode);
      authMode = null;
    }
  };

  const askIdentity = (after) => new Promise((resolve) => {
    pending.current = { after, resolve };
    setAskRegister(true);
  });

  const onSend = (drafts) => {
    if (getPToken() || known.current) {
      enqueue(drafts);
      return Promise.resolve(true);
    }
    return askIdentity((mode) => enqueue(drafts, mode));
  };

  const retry = (item) => {
    if (getPToken() || known.current) return dispatch(item);
    return askIdentity((mode) => dispatch(item, mode));
  };

  const finishRegister = async (mode) => {
    setAskRegister(false);
    const p = pending.current;
    pending.current = null;
    await load();
    if (p) {
      p.resolve(true);
      p.after(mode);
    }
  };

  const cancelRegister = () => {
    setAskRegister(false);
    pending.current?.resolve(false);
    pending.current = null;
  };

  return (
    <div className="flex h-[100dvh] justify-center bg-[#d9d3c7]">
      <div data-testid="participant-chat" className="flex h-full w-full max-w-3xl flex-col overflow-hidden bg-white shadow-sm">
        <Header settings={settings} me={me} onAccount={() => setAccount(true)} />
        {me && <Greeting me={me} />}
        <PushPrompt me={me} />
        <main ref={scroller} className="chat-canvas scroll-thin flex-1 overflow-y-auto">
          <div className="pt-4">
            <Bubble msg={WELCOME} mine={false} footer={null}><p className="mb-0.5 text-sm font-semibold text-zap-green">Equipe IUDP</p></Bubble>
          </div>
          <ParticipantMessages messages={messages} client={publicApi} onDelete={async (id) => {
            try { await publicApi.delete(`/public/messages/${id}`); toast.success("Mensagem apagada."); load(); } catch (e) { toast.error(errMsg(e)); }
          }} onHide={async (id) => {
            try { await publicApi.post(`/public/messages/${id}/hide`); toast.success("Mensagem apagada para você."); load(); } catch (e) { toast.error(errMsg(e)); }
          }} onEdit={async (id, text) => {
            try { await publicApi.patch(`/public/messages/${id}`, { text }); toast.success("Mensagem corrigida."); load(); return true; } catch (e) { toast.error(errMsg(e)); return false; }
          }} />
          <Outbox items={outbox} onRetry={retry} onDiscard={(it) => setOutbox((l) => l.filter((x) => x.localId !== it.localId))} />
        </main>
        <PixBar pix={settings?.pix} />
        <Composer onSend={onSend} />
      </div>
      <RegisterDialog open={askRegister} onClose={cancelRegister} onDone={finishRegister} />
      <AccountDialog open={account} onClose={() => setAccount(false)} me={me}
        onChanged={(out) => (out ? (setMe(null), setMessages([])) : load())} />
    </div>
  );
}
